import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, HeartHandshake, CreditCard, Users, ArrowRight, MapPin } from "lucide-react";
import Emblem from "../../components/Emblem";
import { Button } from "../../components/ui/button";
import { useApi } from "../../hooks/useApi";
import { useLang } from "../../i18n/LanguageContext";
import { FollowStrip } from "../../components/FollowStrip";
import { NewMemberStrip } from "../../components/NewMemberStrip";

const fade = { hidden: { opacity: 0, y: 24 }, show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }) };

export default function Home() {
  const { data: events } = useApi("/events?upcoming=true");
  const { data: anns } = useApi("/announcements");
  const featured = (events || []).slice(0, 3);
  const { t, lang } = useLang();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1660675133902-acd1b057f75d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-24 lg:grid-cols-12 lg:px-8 lg:py-32">
          <motion.div className="lg:col-span-7" initial="hidden" animate="show">
            <motion.p variants={fade} custom={0} className="eyebrow text-gold">{t("Est. 2010 · Affiliated with OKUSACA")}</motion.p>
            <motion.h1 variants={fade} custom={1}
              className="mt-4 font-serif-display text-5xl font-bold leading-none tracking-tight text-parchment sm:text-6xl lg:text-7xl">
              Ofie! <span className="text-gold">Biakoye!</span>
            </motion.h1>
            <motion.p variants={fade} custom={2} className="mt-3 font-serif-display text-2xl italic text-gold/90">{t("Home! Unity!")}</motion.p>
            <motion.p variants={fade} custom={3} className="mt-6 max-w-xl text-lg text-parchment/85">
              {t("The Okuapemman Association of Massachusetts unites sons and daughters of Okuapem in New England — preserving our heritage, celebrating our festivals, and caring for one another through welfare and community.")}
            </motion.p>
            <motion.div variants={fade} custom={4} className="mt-9 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-gold text-crimson hover:bg-gold/90 font-semibold" data-testid="hero-paydues-btn">
                <Link to="/portal/dues">{t("Pay Dues")} <ArrowRight size={18} className="ml-1" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-parchment/40 bg-white/5 text-parchment hover:bg-white/10" data-testid="hero-join-btn">
                <Link to="/registration">{t("Become a Member")}</Link>
              </Button>
            </motion.div>
          </motion.div>
          <motion.div className="hidden justify-center lg:col-span-5 lg:flex"
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <div className="rounded-full bg-parchment/10 p-8 backdrop-blur-sm"><Emblem size={280} /></div>
          </motion.div>
        </div>
        <div className="kente-border" />
      </section>

      {/* QUICK ACTIONS */}
      <section className="mx-auto -mt-12 max-w-7xl px-6 lg:px-8">
        <div className="grid gap-5 md:grid-cols-4">
          {[
            { icon: CreditCard, t: "Pay Dues", d: "Settle dues & levies online", to: "/portal/dues", c: "text-crimson" },
            { icon: Calendar, t: "Events", d: "Meetings & Events", to: "/events", c: "text-gold" },
            { icon: HeartHandshake, t: "Welfare", d: "Benefits & support", to: "/welfare", c: "text-palm" },
            { icon: Users, t: "Membership", d: "Join our family", to: "/membership", c: "text-crimson" },
          ].map((a, i) => (
            <Link key={a.t} to={a.to} data-testid={`quick-${a.t.toLowerCase().replace(' ','-')}`}
              className="card-hover glass rounded-2xl p-6">
              <a.icon className={`h-8 w-8 ${a.c}`} />
              <h3 className="mt-4 font-serif-display text-xl font-bold">{t(a.t)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(a.d)}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* WELCOME */}
      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="eyebrow text-gold">{t("Akwaaba · Welcome")}</p>
            <h2 className="mt-3 font-serif-display text-4xl font-bold tracking-tight text-crimson">{t("A family away from home")}</h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/80">
              {t("Since 2010, we have grown from a handful of families into a thriving association of members across Massachusetts. We are rooted in the traditions of Okuapem in the Eastern Region of Ghana — from the Odwira and Ohum festivals to our shared commitment to unity, mutual support, and cultural pride.")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-foreground/80">
              {t("Whether you are a lineage member or an associate, our doors are open. Together we honor our ancestors, raise our children in our culture, and stand by one another in times of joy and sorrow.")}
            </p>
            <Button asChild className="mt-8 bg-crimson hover:bg-crimson-hover" data-testid="welcome-learn-btn">
              <Link to="/about">{t("Our Story")} <ArrowRight size={16} className="ml-1" /></Link>
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1634074967416-dd24d702c93c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
              alt="Cultural celebration" className="h-64 w-full rounded-2xl object-cover shadow-lg" />
            <img src="https://images.unsplash.com/photo-1660675134062-7d3bbb340608?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
              alt="Community gathering" className="mt-8 h-64 w-full rounded-2xl object-cover shadow-lg" />
          </div>
        </div>
      </section>

      {/* FEATURED EVENTS */}
      <section className="bg-slate-950 py-24 text-parchment">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-end justify-between">
            <div>
              <p className="eyebrow text-gold">{t("Mark your calendar")}</p>
              <h2 className="mt-2 font-serif-display text-4xl font-bold">{t("Upcoming Events")}</h2>
            </div>
            <Button asChild variant="outline" className="border-gold/40 bg-transparent text-gold hover:bg-gold/10" data-testid="view-all-events">
              <Link to="/events">{t("View all")}</Link>
            </Button>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {featured.length === 0 && <p className="text-parchment/60">{t("No upcoming events yet.")}</p>}
            {featured.map((e) => (
              <Link key={e.id} to={`/events/${e.id}`} data-testid={`featured-event-${e.id}`}
                className="card-hover rounded-2xl border border-white/10 bg-white/5 p-6">
                <span className="eyebrow text-gold">{e.type}</span>
                <h3 className="mt-3 font-serif-display text-2xl font-bold">{e.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-parchment/70">{e.description}</p>
                <div className="mt-4 flex items-center gap-2 text-sm text-gold"><Calendar size={15} /> {e.date}</div>
                <div className="mt-1 flex items-center gap-2 text-sm text-parchment/60"><MapPin size={15} /> {e.location}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <NewMemberStrip />

      {/* ANNOUNCEMENTS STRIP */}
      {anns?.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <p className="eyebrow text-gold">{t("Latest from the Association")}</p>
          <h2 className="mt-2 font-serif-display text-4xl font-bold tracking-tight text-crimson">{t("Announcements")}</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {anns.slice(0, 4).map((a) => (
              <div key={a.id} className="card-hover glass rounded-2xl p-6" data-testid={`announcement-${a.id}`}>
                <div className="flex items-center gap-2">
                  {a.pinned && <span className="rounded bg-crimson px-2 py-0.5 text-[10px] font-semibold text-parchment">PINNED</span>}
                  <span className="eyebrow text-gold">{a.category}</span>
                </div>
                <div className="mt-2 flex items-start gap-3">
                  {a.image_url && <img src={a.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${a.image_url}` : a.image_url} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-gold/50" data-testid={`announcement-photo-${a.id}`} />}
                  <div>
                    <h3 className="font-serif-display text-xl font-bold">{a.title}</h3>
                    <p className="mt-2 text-sm text-foreground/75">{(lang === "tw" && a.body_twi) || a.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <FollowStrip />
    </div>
  );
}
